import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Modal, Button } from "react-bootstrap";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCar,
  FaCalendarAlt,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

import sherLogo from "../../img/sher_logo.webp";
import "./style.css";

const BookNow = ({ onClose }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    pickupLocation: "",
    dropoffLocation: "",
    pickupDate: "",
    pickupTime: "",
    returnDate: "",
    returnTime: "",
    numberOfPassengers: "1",
    additionalInfo: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };



  const sendEmail = async (bookingData) => {
    // Using custom backend API
    try {
      // API endpoint - update this to match your backend URL
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${apiUrl}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle specific HTTP status codes with friendly messages
        if (response.status === 400) {
          return { 
            success: false, 
            errorType: 'validation',
            error: t("book_now.error_validation") || "We noticed some information might be missing or incorrect. Please check your details and try again."
          };
        } else if (response.status === 500) {
          return { 
            success: false, 
            errorType: 'server',
            error: t("book_now.error_server") || "We're experiencing technical difficulties on our end. Please try again in a few moments, or contact us directly."
          };
        } else {
          return { 
            success: false, 
            errorType: 'unknown',
            error: errorData.message || t("book_now.error_unknown") || "Something went wrong while processing your booking. Please try again."
          };
        }
      }

      const result = await response.json();
      return { success: true, data: result };
    } catch (error) {
      console.error('Booking submission failed:', error);
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return { 
          success: false, 
          errorType: 'network',
          error: t("book_now.error_network") || "We're having trouble connecting to our booking system. Please check your internet connection and try again."
        };
      }
      
      // Generic error with polite message
      return { 
        success: false, 
        errorType: 'general',
        error: t("book_now.error_general") || "We apologize, but we couldn't complete your booking at this time. Please try again or contact us directly for assistance."
      };
    }
  };

  const saveBookingLocally = (bookingData) => {
    // Save to localStorage as backup
    try {
      const existingBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      const newBooking = {
        ...bookingData,
        id: Date.now(),
        timestamp: new Date().toISOString(),
      };
      existingBookings.push(newBooking);
      localStorage.setItem('bookings', JSON.stringify(existingBookings));
      return true;
    } catch (error) {
      console.error('Failed to save booking locally:', error);
      return false;
    }
  };

  const SubmitHandler = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Prepare booking data
    const bookingData = {
      ...formData,
    };

    // Save locally first
    saveBookingLocally(bookingData);

    // Send email
    const emailResult = await sendEmail(bookingData);

    if (emailResult.success) {
      setModalContent({
        type: 'success',
        message: t("book_now.success") || "Booking submitted successfully! We'll contact you soon."
      });
      setShowModal(true);
      // Reset form after successful submission
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        pickupLocation: "",
        dropoffLocation: "",
        pickupDate: "",
        pickupTime: "",
        returnDate: "",
        returnTime: "",
        numberOfPassengers: "1",
        additionalInfo: "",
      });
    } else {
      // Provide helpful error message with contact information
      const errorMessage = emailResult.error + "\n\n" + (t("book_now.error_contact") || "If this problem persists, please contact us at info@shereastafrica.com or call +255 123 456 789.");
      
      setModalContent({
        type: 'error',
        message: errorMessage
      });
      setShowModal(true);
    }

    setIsSubmitting(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <section className="gauto-book-now section_70">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10}>
            <div className="book-now-wrapper">
              <div className="book-now-header">
                <img src={sherLogo} alt="Sher East Africa" className="book-now-logo" />
                <h2>
                  Book Your Safari Adventure
                </h2>
                <p>Experience Tanzania's Wildlife in Luxury Land Cruiser 70 Series Vehicles</p>
              </div>



              <form onSubmit={SubmitHandler} className="book-now-form">
                {/* Personal Information */}
                <div className="form-section">
                  <h4>{t("book_now.personal_info") || "Personal Information"}</h4>
                  <Row>
                    <Col md={6}>
                      <div className="single-book-field">
                        <label>
                          <FaUser /> {t("book_now.full_name") || "Full Name"} *
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          placeholder={t("book_now.full_name_placeholder") || "Enter your full name"}
                          required
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="single-book-field">
                        <label>
                          <FaEnvelope /> {t("book_now.email") || "Email Address"} *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder={t("book_now.email_placeholder") || "your.email@example.com"}
                          required
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <div className="single-book-field">
                        <label>
                          <FaPhone /> {t("book_now.phone") || "Phone Number"} *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder={t("book_now.phone_placeholder") || "+255 123 456 789"}
                          required
                        />
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Safari Details */}
                <div className="form-section">
                  <h4>{t("book_now.booking_details") || "Safari Details"}</h4>
                  <Row>
                    <Col md={6}>
                      <div className="single-book-field">
                        <label>
                          <FaMapMarkerAlt /> {t("book_now.pickup_location") || "Pickup Location"} *
                        </label>
                        <input
                          type="text"
                          name="pickupLocation"
                          value={formData.pickupLocation}
                          onChange={handleInputChange}
                          placeholder="e.g., Kilimanjaro Airport, Arusha Hotel"
                          required
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="single-book-field">
                        <label>
                          <FaMapMarkerAlt /> {t("book_now.dropoff_location") || "Safari Destination / Drop-off"} *
                        </label>
                        <input
                          type="text"
                          name="dropoffLocation"
                          value={formData.dropoffLocation}
                          onChange={handleInputChange}
                          placeholder="e.g., Serengeti, Ngorongoro Crater"
                          required
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="single-book-field">
                        <label>
                          <FaCalendarAlt /> {t("book_now.pickup_date") || "Safari Start Date"} *
                        </label>
                        <input
                          type="date"
                          name="pickupDate"
                          value={formData.pickupDate}
                          onChange={handleInputChange}
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="single-book-field">
                        <label>
                          <FaCalendarAlt /> {t("book_now.pickup_time") || "Pickup Time"} *
                        </label>
                        <input
                          type="time"
                          name="pickupTime"
                          value={formData.pickupTime}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="single-book-field">
                        <label>
                          <FaCalendarAlt /> {t("book_now.return_date") || "Safari End Date / Return"} *
                        </label>
                        <input
                          type="date"
                          name="returnDate"
                          value={formData.returnDate}
                          onChange={handleInputChange}
                          min={formData.pickupDate || new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="single-book-field">
                        <label>
                          <FaCalendarAlt /> {t("book_now.return_time") || "Return Time"} *
                        </label>
                        <input
                          type="time"
                          name="returnTime"
                          value={formData.returnTime}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <div className="single-book-field">
                        <label>{t("book_now.passengers") || "Number of Passengers"}</label>
                        <select
                          name="numberOfPassengers"
                          value={formData.numberOfPassengers}
                          onChange={handleInputChange}
                        >
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5">5</option>
                          <option value="6">6</option>
                          <option value="7+">7+</option>
                        </select>
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <div className="single-book-field">
                        <label>{t("book_now.additional_info") || "Additional Information"}</label>
                        <textarea
                          name="additionalInfo"
                          value={formData.additionalInfo}
                          onChange={handleInputChange}
                          placeholder={t("book_now.additional_placeholder") || "Any special requests or additional information..."}
                          rows="4"
                        />
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Submit Button */}
                <Row>
                  <Col md={12}>
                    <div className="book-now-submit">
                      <button
                        type="submit"
                        className="gauto-theme-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner"></span> {t("book_now.submitting") || "Submitting..."}
                          </>
                        ) : (
                          <>
                            <FaPaperPlane /> {t("book_now.submit") || "Submit Booking"}
                          </>
                        )}
                      </button>
                    </div>
                  </Col>
                </Row>
              </form>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Success/Error Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton className={modalContent.type === 'success' ? 'modal-success' : 'modal-error'}>
          <Modal.Title>
            {modalContent.type === 'success' ? (
              <><FaCheckCircle /> {t("book_now.success_title") || "Success!"}</>
            ) : (
              <><FaExclamationTriangle /> {t("book_now.error_title") || "We're Sorry"}</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalContent.message}
        </Modal.Body>
        <Modal.Footer>
          <Button variant={modalContent.type === 'success' ? 'success' : 'warning'} onClick={handleCloseModal}>
            {t("book_now.close") || "Close"}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default BookNow;
